import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useCanManageProfitability } from "@/profitability/access";
import { MetaList } from "@/profitability/meta-list";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import { reportError } from "@/profitability/utils";
import {
  defaultPayPeriodAnchor,
  PayPeriodStartSelect,
} from "./pay-period-start-select";
import { useReportCalendar, useUpdateCalendar } from "./query";
import {
  describeCalendar,
  PERIOD_TYPE_LABEL,
  type ReportCalendar,
  WEEKDAYS,
} from "./types";

const DAY_LABELS = Object.fromEntries(
  WEEKDAYS.map((day, index) => [String(index), day]),
);

export const CalendarDialog = ({
  open,
  onOpenChange,
  calendar,
  pending,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calendar: ReportCalendar;
  pending: boolean;
  onSave: (calendar: ReportCalendar) => void;
}) => {
  const [draft, setDraft] = useState(calendar);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-2rem)] max-w-sm overflow-y-auto overscroll-contain",
          SETTINGS_CONTROLS,
        )}
      >
        <DialogHeader>
          <DialogTitle>Report dates</DialogTitle>
        </DialogHeader>
        <Stack
          render={<fieldset disabled={pending} />}
          className="grid grid-cols-[7rem_1fr] items-center gap-3 text-sm"
        >
          <span id="calendar-period">Period</span>
          <Select
            value={draft.period_type}
            items={PERIOD_TYPE_LABEL}
            onValueChange={(value) =>
              setDraft({
                ...draft,
                period_type: value as ReportCalendar["period_type"],
                // A pay period needs a first day; the week's start is the likely one.
                period_anchor_date:
                  draft.period_anchor_date ??
                  (value === "biweekly"
                    ? defaultPayPeriodAnchor(draft.week_starts_on)
                    : null),
              })
            }
          >
            <SelectTrigger aria-labelledby="calendar-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
              {Object.entries(PERIOD_TYPE_LABEL).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {draft.period_type === "weekly" && (
            <>
              <span id="calendar-start">Week starts</span>
              <Select
                value={String(draft.week_starts_on)}
                items={DAY_LABELS}
                onValueChange={(value) =>
                  setDraft({ ...draft, week_starts_on: Number(value) })
                }
              >
                <SelectTrigger aria-labelledby="calendar-start">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
                  {WEEKDAYS.map((day, index) => (
                    <SelectItem key={day} value={String(index)}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          {draft.period_type === "biweekly" && (
            <>
              <span id="calendar-anchor">Pay period starts</span>
              <PayPeriodStartSelect
                labelledBy="calendar-anchor"
                anchor={draft.period_anchor_date ?? null}
                weekStartsOn={draft.week_starts_on}
                onChange={(anchor) =>
                  setDraft({ ...draft, period_anchor_date: anchor })
                }
              />
            </>
          )}
        </Stack>
        {draft.period_type === "biweekly" && (
          <Text variant="caption" tone="muted">
            Pick the first day of any pay period. Every period runs 14 days from
            it, so new runs suggest the next pay period.
          </Text>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            loading={pending}
            disabled={
              draft.period_type === "biweekly" && !draft.period_anchor_date
            }
            onClick={() => onSave(draft)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The template's reporting calendar in one line, and the dialog that changes
 * it. New runs propose their dates from it, so a business whose week starts
 * on Sunday is never handed Monday to Sunday.
 */
export const CalendarLine = ({
  businessId,
  reportId,
}: {
  businessId: string;
  reportId: string;
}) => {
  const calendar = useReportCalendar(businessId, reportId);
  const update = useUpdateCalendar(businessId, reportId);
  const [editing, setEditing] = useState(false);
  // Changing the calendar is finance's (Profitability::Access).
  const canManage = useCanManageProfitability();
  if (!calendar.data) return null;
  return (
    <div className="flex items-center gap-2">
      <Text variant="caption" tone="muted">
        <MetaList>
          {describeCalendar(calendar.data)}
          {!calendar.data.timezone_is_business_default &&
            calendar.data.timezone}
        </MetaList>
      </Text>
      {canManage && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setEditing(true)}
        >
          Change
        </Button>
      )}
      {editing && (
        <CalendarDialog
          open
          onOpenChange={setEditing}
          calendar={calendar.data}
          pending={update.isPending}
          onSave={(next) =>
            update.mutate(next, {
              onSuccess: () => setEditing(false),
              onError: (error) =>
                toast.error("The calendar could not be saved", {
                  description: reportError(error),
                }),
            })
          }
        />
      )}
    </div>
  );
};
